/**
 * <reviews-list> — review loading, sorting and variant filtering.
 *
 * Two view contexts:
 *  - "All" (filter empty): the server-rendered cached reviews (newest ~30).
 *    "Show more" pages further product-level reviews from the public API.
 *  - "Variant" (a SKU selected): the list is replaced with that variant's
 *    reviews fetched from the API by SKU, with the variant's true total and
 *    full pagination. Clearing the filter restores the cached list.
 *
 * Reviews are de-duplicated by review id, pages are tracked explicitly, and a
 * short page from the API marks the end. Only the public API is used (store
 * key only — no secrets). On a fetch problem the button hides; server-rendered
 * content is untouched.
 */
const SVG_NS = 'http://www.w3.org/2000/svg';

class ReviewsIoList extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[ref="list"]');
    this.button = this.querySelector('[ref="moreButton"]');
    this.filterEl = this.querySelector('[ref="filter"]');
    this.sortEl = this.querySelector('[ref="sort"]');
    this.emptyEl = this.querySelector('[ref="empty"]');
    this.loaderEl = this.querySelector('[ref="loader"]');
    if (!this.list) return;

    this.perPage = Math.max(1, Number(this.dataset.initialCount) || 5);
    this.store = this.dataset.store || '';
    this.allQuery = this.dataset.query || '';
    this.productTotal = Number(this.dataset.total) || 0;
    // Aggregate (tasting/bundle): the combined total comes from the server (the
    // API can't sum across the included variants), so don't let a page's
    // stats.count override it.
    this.aggregate = this.dataset.aggregate === 'true';
    try {
      this.skuTitles = JSON.parse(this.dataset.skuTitles || '{}');
    } catch {
      this.skuTitles = {};
    }
    try {
      this.timeAgo = JSON.parse(this.dataset.timeago || '{}');
    } catch {
      this.timeAgo = {};
    }

    this.cachedArticles = this.articles;
    this.activeSku = '';
    this.activeTotal = this.productTotal;
    this.visibleTarget = this.perPage;
    this.ratingFilter = 0; // 0 = all stars; set by clicking the breakdown bars
    this.ratingTarget = 0; // true count for the active star (from the breakdown)
    this.apiExhausted = false;
    this.resetTracking(this.cachedArticles);

    this.button?.addEventListener('click', () => this.showMore());
    this.sortEl?.addEventListener('change', () => {
      this.visibleTarget = this.perPage;
      this.apply();
    });
    this.filterEl?.addEventListener('change', () => this.onFilter());

    if (this.dataset.mode === 'client' && this.store) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          observer.disconnect();
          if (!this.aggregate) this.activeTotal = 0; // aggregate keeps the server total
          this.setLoading(true);
          this.fetchPage(true).then(() => {
            this.setLoading(false);
            this.apply();
          });
        },
        { rootMargin: '400px' }
      );
      observer.observe(this);
    }

    // Client mode loads via the observer above; applying now (empty list) would
    // flash the "no reviews" message. refs mode applies immediately.
    if ((this.filterEl || this.sortEl) && this.dataset.mode !== 'client') this.apply();
  }

  get articles() {
    return [...this.list.querySelectorAll('article.reviews-review')];
  }

  get loadedCount() {
    return this.articles.length;
  }

  /** Seed the de-dup id set and page cursor for the current context. */
  resetTracking(seedArticles) {
    this.seenIds = new Set((seedArticles || []).map((a) => a.dataset.reviewId).filter(Boolean));
    // Cached reviews occupy the first ceil(count/perPage) API pages.
    this.page = Math.ceil((seedArticles ? seedArticles.length : 0) / this.perPage);
  }

  setLoading(on) {
    this.classList.toggle('reviews-list--loading', on);
    if (this.loaderEl) this.loaderEl.hidden = !on;
    if (this.button) this.button.disabled = on;
  }

  setArticles(nodes) {
    for (const a of this.articles) a.remove();
    for (const n of nodes) this.list.insertBefore(n, this.emptyEl || null);
  }

  onFilter() {
    this.activeSku = this.filterEl.value || '';
    this.refresh();
  }

  /** (Re)load the current context: variant filter and/or exact-star filter. */
  async refresh() {
    this.visibleTarget = this.perPage;

    // Nothing selected → back to the "All" list.
    if (!this.activeSku && !this.ratingFilter) {
      this.activeTotal = this.productTotal;
      if (this.dataset.mode === 'client' && this.store) {
        // No server-rendered cache to restore (aggregate/client) — reload the
        // combined set from the API.
        this.seenIds = new Set();
        this.page = 0;
        this.apiExhausted = false;
        this.setArticles([]);
        this.setLoading(true);
        await this.fetchToFill();
        this.setLoading(false);
      } else {
        this.resetTracking(this.cachedArticles);
        this.setArticles(this.cachedArticles);
      }
      this.apply();
      return;
    }

    // No public API → best-effort client filter over what's already loaded.
    if (!this.store) {
      this.apply();
      return;
    }

    // Fetch a fresh result set from the API (by SKU, optionally by exact star).
    this.seenIds = new Set();
    this.page = 0;
    this.apiExhausted = false;
    // Star filter: the true total is the breakdown count (stats.count is the
    // product total, unfiltered). Variant-only total is learned from the API.
    this.activeTotal = this.ratingFilter ? this.ratingTarget : 0;
    this.setArticles([]);
    this.setLoading(true);
    await this.fetchToFill();
    this.setLoading(false);
    this.apply();
  }

  /** Page the API until the visible target is met or results run out. */
  async fetchToFill() {
    if (!this.store) return;
    let guard = 0;
    while (this.loadedCount < this.visibleTarget && !this.apiExhausted && guard < 60) {
      const before = this.loadedCount;
      await this.fetchPage(false);
      guard += 1;
      if (this.loadedCount === before) break; // no progress (error / empty page)
    }
  }

  apply() {
    const sort = this.sortEl?.value || 'newest';
    const sorted = this.articles.sort((a, b) => {
      if (sort === 'highest') return ratingOf(b) - ratingOf(a);
      return dateOf(b) - dateOf(a); // newest
    });
    for (const a of sorted) this.list.insertBefore(a, this.emptyEl || null);

    let shown = 0;
    for (const a of sorted) {
      const matchesRating = !this.ratingFilter || ratingOf(a) === this.ratingFilter;
      if (matchesRating && shown < this.visibleTarget) {
        a.hidden = false;
        shown += 1;
      } else {
        a.hidden = true;
      }
    }

    const matchingCount = this.ratingFilter
      ? sorted.filter((a) => ratingOf(a) === this.ratingFilter).length
      : this.loadedCount;
    if (this.emptyEl) this.emptyEl.hidden = matchingCount !== 0;

    if (this.button) {
      const moreLoaded = matchingCount > this.visibleTarget;
      const canFetch = Boolean(this.store) && !this.apiExhausted && this.loadedCount < this.activeTotal;
      this.button.parentElement.hidden = !(moreLoaded || canFetch);
      const tpl = this.dataset.labelViewAll;
      if (tpl) this.button.textContent = tpl.replace('999999', String(this.activeTotal || matchingCount));
    }
  }

  async toggleRatingFilter(rating, count) {
    const turningOff = this.ratingFilter === rating;
    this.ratingFilter = turningOff ? 0 : rating;
    this.ratingTarget = turningOff ? 0 : (Number(count) || 0);
    document.querySelectorAll('[data-reviews-rating]').forEach((b) => {
      const on = !turningOff && Number(b.dataset.reviewsRating) === this.ratingFilter;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    await this.refresh();
  }

  async showMore() {
    this.visibleTarget += this.perPage;
    if (this.store && !this.apiExhausted && this.loadedCount < this.visibleTarget && this.loadedCount < this.activeTotal) {
      this.setLoading(true);
      await this.fetchToFill();
      this.setLoading(false);
    }
    this.apply();
  }

  async fetchPage(reset) {
    if (!this.store) return;
    const sku = this.activeSku || this.allQuery;
    if (!sku) return;
    if (reset) this.page = 0;
    this.page += 1;

    const url = new URL('https://api.reviews.io/product/review');
    url.searchParams.set('store', this.store);
    url.searchParams.set('sku', sku);
    url.searchParams.set('per_page', String(this.perPage));
    url.searchParams.set('page', String(this.page));
    if (this.ratingFilter) {
      // Exact star = minRating == maxRating (both confirmed supported).
      url.searchParams.set('minRating', String(this.ratingFilter));
      url.searchParams.set('maxRating', String(this.ratingFilter));
    }

    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // stats.count is the product total (unfiltered) — only adopt it for a
      // variant context whose total we don't yet know (not for all / star).
      if (!this.ratingFilter && this.activeTotal === 0) {
        const c = Number(json?.stats?.count ?? json?.reviews?.total ?? 0);
        if (c) this.activeTotal = c;
      }
      const reviews = json?.reviews?.data ?? [];
      for (const review of reviews) {
        const id = String(review.product_review_id ?? review.id ?? '');
        if (id && this.seenIds.has(id)) continue;
        if (id) this.seenIds.add(id);
        const flavor = this.skuTitles[String(review.sku ?? '')] || '';
        this.list.insertBefore(this.renderReview(review, flavor, id), this.emptyEl || null);
      }
      // A short page is the API's end-of-results signal.
      if (reviews.length < this.perPage) {
        this.apiExhausted = true;
        if (this.loadedCount < this.activeTotal) this.activeTotal = this.loadedCount;
      }
    } catch {
      this.apiExhausted = true; // stop offering; loaded reviews remain
    }
  }

  /**
   * Relative "time ago" string, matching snippets/reviews-timeago.liquid
   * exactly (same thresholds + translation strings) so API-loaded cards read
   * identically to the server-rendered ones ("vor 1 Woche", not "letzte Woche").
   */
  relativeTime(iso) {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return '';
    let diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 0) diff = 0;

    const ta = this.timeAgo || {};
    const pick = (key, n) => {
      const tpl = ta[key];
      if (!tpl) return '';
      if (typeof tpl === 'string') return tpl;
      const form = n === 1 ? tpl.one : tpl.other;
      return String(form ?? '').replace(n === 1 ? '1' : '2', String(n));
    };

    if (diff < 60) return ta.now || '';
    if (diff < 3600) return pick('minutes', Math.floor(diff / 60));
    if (diff < 86400) return pick('hours', Math.floor(diff / 3600));
    if (diff < 604800) return pick('days', Math.floor(diff / 86400));
    if (diff < 2592000) return pick('weeks', Math.floor(diff / 604800));
    if (diff < 31536000) return pick('months', Math.floor(diff / 2592000));
    return pick('years', Math.floor(diff / 31536000));
  }

  renderReview(review, flavorLabel, id) {
    const rating = Math.max(0, Math.min(5, Number(review.rating) || 0));
    const reviewer = review.reviewer ?? {};
    const firstName = (reviewer.first_name ?? '').trim();
    const lastInitial = (reviewer.last_name ?? '').trim().slice(0, 1);
    const author = firstName
      ? `${firstName}${lastInitial ? ` ${lastInitial}.` : ''}`
      : this.dataset.labelAnonymous || '';
    const verified = reviewer.verified_buyer === 'yes' || reviewer.verified_buyer === true;
    const location = (reviewer.address ?? '').replace(/"/g, '').trim();
    const iso = (review.date_created ?? '').slice(0, 19);

    const article = document.createElement('article');
    article.className = 'reviews-review';
    article.dataset.rating = String(rating);
    article.dataset.date = iso;
    article.dataset.flavor = (flavorLabel || '').toLowerCase();
    if (id) article.dataset.reviewId = id;

    const head = el('div', 'reviews-review__head');
    head.appendChild(this.renderStars(rating));
    if (iso) {
      const time = document.createElement('time');
      time.className = 'reviews-review__date';
      time.dateTime = iso;
      time.textContent = this.relativeTime(iso);
      head.appendChild(time);
    }
    article.appendChild(head);

    // Matches snippets/reviews-card.liquid: author · location · verified · tag.
    const meta = el('div', 'reviews-review__meta');
    meta.appendChild(el('span', 'reviews-review__author', author));
    if (location) meta.appendChild(el('span', 'reviews-review__loc', `· ${location}`));
    if (verified) meta.appendChild(this.renderVerified());
    if (flavorLabel) meta.appendChild(el('span', 'reviews-review__tag', flavorLabel));
    article.appendChild(meta);

    const title = (review.title ?? '').trim();
    if (title) article.appendChild(el('p', 'reviews-review__title', title));

    const body = (review.review ?? review.comments ?? '').trim();
    if (body) {
      article.appendChild(el('p', 'reviews-review__body', body));
    } else if (!title && this.dataset.labelNoComment) {
      article.appendChild(el('p', 'reviews-review__body reviews-review__body--empty', this.dataset.labelNoComment));
    }

    return article;
  }

  /** Verified badge: check in a primary-colour circle (matches the snippet). */
  renderVerified() {
    const wrap = el('span', 'reviews-review__verified');
    const icon = el('span', 'reviews-verified-icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    wrap.appendChild(icon);
    wrap.appendChild(document.createTextNode(this.dataset.labelVerified || ''));
    return wrap;
  }

  /** Build SVG stars directly (matches the server-rendered snippet, same colours). */
  renderStars(rating) {
    const span = document.createElement('span');
    span.className = 'reviews-stars';
    const filled = Math.round(rating);
    for (let i = 1; i <= 5; i += 1) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', `reviews-star${i <= filled ? ' reviews-star--filled' : ''}`);
      svg.setAttribute('viewBox', '0 0 32 32');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.setProperty('--reviews-star-size', '1rem');
      const use = document.createElementNS(SVG_NS, 'use');
      use.setAttribute('href', '#reviews-star-symbol');
      svg.appendChild(use);
      span.appendChild(svg);
    }
    return span;
  }
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function ratingOf(article) {
  return Number(article.dataset.rating) || 0;
}

function dateOf(article) {
  const t = Date.parse(article.dataset.date || '');
  return Number.isNaN(t) ? 0 : t;
}

// Clicking a rating-breakdown bar (a separate block) filters the review list by
// that star count. The breakdown only needs data-reviews-rating buttons; this
// document listener finds the list and toggles the filter.
function wireBreakdown() {
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-reviews-rating]');
    if (!btn) return;
    const list = document.querySelector('reviews-list');
    if (list && typeof list.toggleRatingFilter === 'function') {
      list.toggleRatingFilter(Number(btn.dataset.reviewsRating), Number(btn.dataset.reviewsCount));
    }
  });
}

if (!customElements.get('reviews-list')) {
  customElements.define('reviews-list', ReviewsIoList);
  wireBreakdown();
}
